import { useCallback, useEffect, useRef, useState } from "react";
import defaultWelcomeOfferService from "./welcomeOfferService";
import {
  createWelcomeOfferSync,
  dispatchWelcomeOfferRefreshEvents,
  welcomeOfferAccountScope,
} from "./welcomeOfferEvents";

const SESSION_DISMISS_PREFIX = "eternua.welcome-offer.dismissed.session.v1";

function sessionDismissKey(accountScope, offerVersion) {
  return `${SESSION_DISMISS_PREFIX}:${accountScope}:${offerVersion || "unversioned"}`;
}

export function rememberSessionDismissal(accountScope, offerVersion) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      sessionDismissKey(accountScope, offerVersion),
      "1"
    );
  } catch (_error) {
    // The in-memory closed state still prevents route-level reopening.
  }
}

export function hasSessionDismissal(accountScope, offerVersion) {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.sessionStorage.getItem(
        sessionDismissKey(accountScope, offerVersion)
      ) === "1"
    );
  } catch (_error) {
    return false;
  }
}

export function isDisplayableWelcomeOffer(offer) {
  return Boolean(
    offer?.displayAllowed === true &&
      offer?.campaignActive === true &&
      offer?.eligible === true &&
      offer?.claimable === true &&
      offer?.alreadyClaimed !== true &&
      offer?.offerCode &&
      Number.isSafeInteger(offer?.amountMinor) &&
      offer.amountMinor > 0 &&
      offer?.currency
  );
}

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `welcome-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function initialState() {
  return {
    phase: "idle",
    isOpen: false,
    offer: null,
    claim: null,
    message: "",
  };
}

function publicClaimError(error) {
  if (error?.status === 429) {
    return "Trop de tentatives rapprochées. Patiente un instant avant de réessayer.";
  }
  return "L’activation n’a pas abouti. Tu peux réessayer sans risque de double crédit.";
}

export default function useWelcomeOffer({
  enabled,
  account,
  service = defaultWelcomeOfferService,
  onSessionExpired,
} = {}) {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);
  const claimLockRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  const syncRef = useRef(null);
  const dismissedDuringClaimRef = useRef(false);
  const onSessionExpiredRef = useRef(onSessionExpired);
  const accountScope = welcomeOfferAccountScope(account);
  const hasAccount = Boolean(account);
  const lifecycleRef = useRef({ accountScope, enabled, hasAccount });
  lifecycleRef.current = { accountScope, enabled, hasAccount };

  const isCurrentAccount = useCallback((expectedAccountScope) => {
    const lifecycle = lifecycleRef.current;
    return Boolean(
      lifecycle.enabled &&
        lifecycle.hasAccount &&
        lifecycle.accountScope === expectedAccountScope
    );
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(
    () => () => {
      // A request may finish after logout, account replacement or route-shell
      // teardown. Mark this hook instance stale before its promise settles.
      lifecycleRef.current = {
        ...lifecycleRef.current,
        enabled: false,
        hasAccount: false,
      };
    },
    []
  );

  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired;
  }, [onSessionExpired]);

  const expireSession = useCallback(() => {
    claimLockRef.current = false;
    idempotencyKeyRef.current = null;
    setState({
      phase: "session_expired",
      isOpen: false,
      offer: null,
      claim: null,
      message: "Ta session a expiré. Reconnecte-toi pour continuer.",
    });
    onSessionExpiredRef.current?.();
  }, []);

  const finalizeSuccess = useCallback(
    (claim, statusOverride = null, { publish = true } = {}) => {
      if (!isCurrentAccount(accountScope)) return;
      const currentOffer = stateRef.current.offer || {};
      const nextOffer = {
        ...currentOffer,
        ...(statusOverride || {}),
        amountMinor:
          claim?.amountMinor ?? statusOverride?.amountMinor ?? currentOffer.amountMinor,
        currency:
          claim?.currency ?? statusOverride?.currency ?? currentOffer.currency,
      };

      // WalletContext consumes the server-confirmed summary from this event.
      // No private balance or transaction is persisted in a browser cache.
      dispatchWelcomeOfferRefreshEvents(claim);
      if (publish) {
        syncRef.current?.publish({
          type: "claimed",
          accountScope,
          offerVersion: nextOffer.offerVersion || "",
        });
      }

      claimLockRef.current = false;
      idempotencyKeyRef.current = null;
      const keepClosed = dismissedDuringClaimRef.current;
      dismissedDuringClaimRef.current = false;
      setState({
        phase: "success",
        isOpen: !keepClosed,
        offer: nextOffer,
        claim: claim || null,
        message: "Ton bonus a été ajouté à ton portefeuille Eternua.",
      });
    },
    [accountScope, isCurrentAccount]
  );

  const reconcile = useCallback(async () => {
    const requestAccountScope = accountScope;
    setState((current) => ({
      ...current,
      phase: "uncertain",
      isOpen: true,
      message: "Nous vérifions l’état canonique avant toute nouvelle tentative.",
    }));

    try {
      const status = await service.getStatus();
      if (!isCurrentAccount(requestAccountScope)) return "stale_account";
      if (status.alreadyClaimed) {
        finalizeSuccess(status.claimResult, status);
        return "claimed";
      }
      if (isDisplayableWelcomeOffer(status)) {
        claimLockRef.current = false;
        setState((current) => ({
          ...current,
          phase: "recoverable",
          isOpen: true,
          offer: status,
          message:
            "Aucune activation n’a été confirmée. Tu peux réessayer en toute sécurité.",
        }));
        return "claimable";
      }

      claimLockRef.current = false;
      idempotencyKeyRef.current = null;
      setState((current) => ({
        ...current,
        phase: "unavailable",
        isOpen: true,
        offer: current.offer || status,
        message:
          String(status.reasonCode || "").toLowerCase() === "expired"
            ? "Cette offre a expiré pendant la vérification."
            : "Cette offre n’est plus disponible.",
      }));
      return "unavailable";
    } catch (error) {
      if (!isCurrentAccount(requestAccountScope)) return "stale_account";
      if (error?.code === "SESSION_EXPIRED") {
        expireSession();
        return "session_expired";
      }
      claimLockRef.current = false;
      setState((current) => ({
        ...current,
        phase: "uncertain",
        isOpen: true,
        message:
          error?.code === "OFFLINE"
            ? "Connexion interrompue : impossible de vérifier l’activation pour le moment."
            : "L’état de l’activation reste incertain. Vérifie à nouveau avant de réessayer.",
      }));
      return "uncertain";
    }
  }, [accountScope, expireSession, finalizeSuccess, isCurrentAccount, service]);

  const claim = useCallback(async () => {
    const requestAccountScope = accountScope;
    const current = stateRef.current;
    if (
      claimLockRef.current ||
      !current.isOpen ||
      !["eligible", "recoverable", "offline"].includes(current.phase)
    ) {
      return;
    }

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setState((value) => ({
        ...value,
        phase: "offline",
        message: "Une connexion internet est nécessaire pour activer le bonus.",
      }));
      return;
    }

    claimLockRef.current = true;
    dismissedDuringClaimRef.current = false;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = createIdempotencyKey();
    }
    setState((value) => ({
      ...value,
      phase: "claiming",
      message: "Activation sécurisée en cours…",
    }));

    try {
      const result = await service.claim({
        idempotencyKey: idempotencyKeyRef.current,
      });
      if (!isCurrentAccount(requestAccountScope)) return;
      finalizeSuccess(result);
    } catch (error) {
      if (!isCurrentAccount(requestAccountScope)) return;
      if (error?.code === "SESSION_EXPIRED") {
        expireSession();
        return;
      }
      if (
        error?.code === "UNCERTAIN" ||
        error?.code === "RECONCILE_REQUIRED"
      ) {
        setState((value) => ({
          ...value,
          phase: "uncertain",
          message:
            "La réponse est incertaine. Eternua vérifie le statut avant d’autoriser une nouvelle tentative.",
        }));
        await reconcile();
        return;
      }
      claimLockRef.current = false;
      if (error?.code !== "OFFLINE") idempotencyKeyRef.current = null;
      setState((value) => ({
        ...value,
        phase: error?.code === "OFFLINE" ? "offline" : "recoverable",
        message:
          error?.code === "OFFLINE"
            ? "Une connexion internet est nécessaire pour activer le bonus."
            : publicClaimError(error),
      }));
    }
  }, [
    accountScope,
    expireSession,
    finalizeSuccess,
    isCurrentAccount,
    reconcile,
    service,
  ]);

  const dismiss = useCallback(
    async (reason = "dismissed") => {
      const current = stateRef.current;
      const offerVersion = current.offer?.offerVersion || "";
      if (current.phase === "claiming") dismissedDuringClaimRef.current = true;
      claimLockRef.current = false;
      setState((value) => ({ ...value, phase: "dismissed", isOpen: false }));
      syncRef.current?.publish({
        type: "dismissed",
        accountScope,
        offerVersion,
      });

      try {
        const result = await service.dismiss({ reason });
        if (!isCurrentAccount(accountScope)) return;
        if (!result?.persisted) {
          rememberSessionDismissal(accountScope, offerVersion);
        }
      } catch (error) {
        if (!isCurrentAccount(accountScope)) return;
        // A failed preference write must not turn dismissal into eligibility.
        // Keep the UI hidden for this browser session, with no invented TTL.
        rememberSessionDismissal(accountScope, offerVersion);
        if (error?.code === "SESSION_EXPIRED") expireSession();
      }
    },
    [accountScope, expireSession, isCurrentAccount, service]
  );

  const closeSuccess = useCallback(() => {
    setState((value) => ({ ...value, isOpen: false, phase: "closed" }));
  }, []);

  useEffect(() => {
    if (!enabled || !hasAccount) {
      claimLockRef.current = false;
      idempotencyKeyRef.current = null;
      setState(initialState());
      return undefined;
    }

    const controller = new AbortController();
    setState({
      phase: "loading",
      isOpen: false,
      offer: null,
      claim: null,
      message: "",
    });

    service
      .getStatus({ signal: controller.signal })
      .then((status) => {
        if (controller.signal.aborted) return;
        if (
          !isDisplayableWelcomeOffer(status) ||
          hasSessionDismissal(accountScope, status.offerVersion)
        ) {
          setState({
            phase: status.alreadyClaimed ? "already_claimed" : "ineligible",
            isOpen: false,
            offer: status,
            claim: null,
            message: "",
          });
          return;
        }
        setState({
          phase: "eligible",
          isOpen: true,
          offer: status,
          claim: null,
          message: "",
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error?.code === "SESSION_EXPIRED") {
          expireSession();
          return;
        }
        // Without canonical status, the offer remains invisible. No demo value
        // or client-side eligibility is substituted.
        setState({
          phase: error?.code === "OFFLINE" ? "offline_status" : "status_error",
          isOpen: false,
          offer: null,
          claim: null,
          message: "",
        });
      });

    return () => controller.abort();
  }, [accountScope, enabled, expireSession, hasAccount, service]);

  useEffect(() => {
    if (!enabled || !hasAccount) return undefined;
    const sync = createWelcomeOfferSync(async (message) => {
      if (message.accountScope !== accountScope) return;
      if (message.type === "dismissed") {
        const offerVersion =
          message.offerVersion || stateRef.current.offer?.offerVersion || "";
        rememberSessionDismissal(accountScope, offerVersion);
        setState((value) => ({ ...value, isOpen: false, phase: "dismissed" }));
        return;
      }
      if (message.type === "claimed") {
        const wasOpen = stateRef.current.isOpen;
        setState((value) => ({ ...value, isOpen: false, phase: "already_claimed" }));
        dispatchWelcomeOfferRefreshEvents(null);
        if (!wasOpen) return;
        try {
          const status = await service.getStatus();
          if (status.alreadyClaimed) {
            finalizeSuccess(status.claimResult, status, { publish: false });
          }
        } catch (_error) {
          // The server remains authoritative; the second tab stays closed.
        }
      }
    });
    syncRef.current = sync;
    return () => {
      sync.close();
      if (syncRef.current === sync) syncRef.current = null;
    };
  }, [accountScope, enabled, finalizeSuccess, hasAccount, service]);

  return {
    ...state,
    claim,
    dismiss,
    reconcile,
    closeSuccess,
  };
}
